import { HttpClient, HttpEventType, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { filter, finalize, map, Observable, tap } from 'rxjs';

import { PaginatedList, ProductPackage, Result } from '@shared/models';

import { BaseService } from '../base.service';
import { httpParams } from '../http-params.builder';
import { LoadersService } from '../loaders.service';
import { Filter, Sort } from '../table-filters.service';

@Injectable({
  providedIn: 'root',
})
export class ProductPackagesService extends BaseService {
  constructor(
    private http: HttpClient,
    private loadersService: LoadersService
  ) {
    super();
  }

  getAll(loader: string): Observable<ProductPackage[]> {
    this.loadersService.show(loader);
    return this.http
      .get<ProductPackage[]>(`${this.baseUrl}/product-packages`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getById(loader: string, id: string): Observable<ProductPackage> {
    this.loadersService.show(loader);
    return this.http
      .get<ProductPackage>(`${this.baseUrl}/product-packages/${id}`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getProjectAssignedProductPackages(
    loader: string,
    id: string,
    excludedAssignmentGuid?: string
  ): Observable<ProductPackage[]> {
    this.loadersService.show(loader);

    return this.http
      .get<ProductPackage[]>(`${this.baseUrl}/product-packages/project/${id}/assigned-product-packages`, {
        params: httpParams().set('excludedAssignmentGuid', excludedAssignmentGuid).build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  get(
    loader: string,
    projectId: string | undefined,
    pageIndex = 0,
    pageSize = 50,
    filters: Filter = null,
    sort: Sort = null
  ): Observable<PaginatedList<ProductPackage>> {
    this.loadersService.show(loader);

    const url = projectId
      ? `${this.baseUrl}/product-packages/paginated/project/${projectId}`
      : `${this.baseUrl}/product-packages/paginated`;

    return this.http
      .get<
        PaginatedList<ProductPackage>
      >(url, { params: httpParams().paging(pageIndex, pageSize).table(filters, sort).build() })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  add(loader: string, dto: ProductPackage): Observable<ProductPackage> {
    this.loadersService.show(loader);
    return this.http
      .post<ProductPackage>(this.baseUrl + '/product-packages', dto)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  edit(loader: string, dto: ProductPackage): Observable<ProductPackage> {
    this.loadersService.show(loader);
    return this.http
      .put<ProductPackage>(this.baseUrl + '/product-packages', dto)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  parse(loader: string, file: File, projectId?: string): Observable<Result<ProductPackage>[]> {
    this.loadersService.show(loader);

    const formData = new FormData();
    formData.append('file', file);
    const headers = new HttpHeaders({ 'ngsw-bypass': '' });

    const url = projectId
      ? `${this.baseUrl}/product-packages/parse/project/${projectId}`
      : `${this.baseUrl}/product-packages/parse`;

    return this.http
      .post<Result<ProductPackage>[]>(url, formData, {
        headers,
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        tap((event) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            this.loadersService.update(loader, Math.round((100 * event.loaded) / event.total));
          }
        }),
        filter((event): event is HttpResponse<Result<ProductPackage>[]> => event.type === HttpEventType.Response),
        map((response) => response.body),
        finalize(() => this.loadersService.hide(loader))
      );
  }

  addOrUpdateParsedItems(loader: string, items: ProductPackage[]): Observable<string[]> {
    this.loadersService.show(loader);
    return this.http
      .put<string[]>(`${this.baseUrl}/product-packages/parse/import`, { items })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  delete(loader: string, id: string): Observable<boolean> {
    this.loadersService.show(loader);
    return this.http.delete(`${this.baseUrl}/product-packages/${id}`, { observe: 'response' }).pipe(
      map((response) => this.isStatusSucceed(response.status)),
      finalize(() => this.loadersService.hide(loader))
    );
  }

  exportEmpty(loader: string) {
    this.loadersService.show(loader);
    return this.http
      .get(`${this.baseUrl}/product-packages/export/empty`, { responseType: 'blob' })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  export(loader: string, projectId: string | undefined) {
    this.loadersService.show(loader);

    const url = projectId
      ? `${this.baseUrl}/product-packages/export/project/${projectId}`
      : `${this.baseUrl}/product-packages/export`;

    return this.http.get(url, { responseType: 'blob' }).pipe(finalize(() => this.loadersService.hide(loader)));
  }
}
